import React, { useEffect, useState } from 'react';
import { get, post } from '../../../Api/http/httpClient';
import {
  Card,
  Button,
  Input,
  Typography,
  Switch,
  message,
  Divider,
  Row,
  Col,
  Tag,
  Space,
  Spin,
  Select
} from 'antd';
import { MailOutlined, PhoneOutlined } from '@ant-design/icons';
import rawData from '../../../utils/data/data.json';
import 'country-flag-icons/3x2/flags.css';
import './NotificationSettings.css';

const { Text, Title } = Typography;
const { Option } = Select;

interface UserData {
  "custom:establishment_id"?: string;
  "family_name"?: string;
  email?: string;
}

interface NotificationSettingsProps {
  user_data: UserData;
}

interface Country {
  id: number;
  name: string;
  iso3: string;
  iso2: string;
  phone_code: string;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ user_data }) => {
  const establishmentID = user_data?.["custom:establishment_id"] || "";
  const name_establishment = user_data?.["family_name"] || "";
  const originalEmail = user_data?.email || "";

  // Estados para celular
  const [celularNumber, setCelularNumber] = useState<string>('');
  const [enableCelular, setEnableCelular] = useState<boolean>(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
  const [countries, setCountries] = useState<Country[]>([]);
  const [celularError, setCelularError] = useState<boolean>(false);
  const [countryCodeError, setCountryCodeError] = useState<boolean>(false);

  // Estados para correo
  const [customEmail, setCustomEmail] = useState<string>(originalEmail);
  const [enableEmail, setEnableEmail] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(true);

  // Cargar países al montar el componente
  useEffect(() => {
    const normalizedCountries = (rawData as any[]).map(country => ({
      id: country.id,
      name: country.name,
      iso3: country.iso3,
      iso2: country.iso2,
      phone_code: country.phone_code
    }));
    setCountries(normalizedCountries);
  }, []);

  // Al montar, obtenemos la configuración existente desde el backend
  useEffect(() => {
    fetchNotificationSettings();
    // eslint-disable-next-line
  }, []);

  // Si el usuario deja vacío el celular, forzamos enableCelular a false
  useEffect(() => {
    if (!celularNumber?.trim()) {
      setEnableCelular(false);
    } else if (celularNumber?.trim().length === 10 && selectedCountryCode) {
      setEnableCelular(true);
    } else {
      setEnableCelular(false);
    }
  }, [celularNumber, selectedCountryCode]);

  // Si el usuario deja vacío el correo, forzamos enableEmail a false
  useEffect(() => {
    if (!customEmail?.trim()) {
      setEnableEmail(false);
    }
  }, [customEmail]);

  // Validar email
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // 1) OBTENER CONFIGURACIÓN GUARDADA
  const fetchNotificationSettings = async () => {
    try {
      setLoading(true);
      setInitialLoading(true);
      const response = await get(`/notification-settings?establishmentID=${establishmentID}&name_establishment=${name_establishment}`);

      if (response.answer) {
        // Separar el código de país y el número
        const [countryCode, phoneNumber] = response.item.celular_number.split('-');
        setSelectedCountryCode(countryCode ? `+${countryCode}` : '');
        setCelularNumber(phoneNumber);
        // Solo activamos el switch si enable_celular es true
        setEnableCelular(response.item.enable_celular);
        setCustomEmail(response.item.email);
        // Solo activamos el switch si enable_email es true
        setEnableEmail(response.item.enable_email);
      } else {
        setCustomEmail(originalEmail);
        message.error("No se encontró la configuración de notificaciones.");
      }
    } catch (error) {
      console.error("Error al obtener configuración de notificaciones:", error);
      message.error("No se pudo obtener la configuración de notificaciones.");
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  // 2) GUARDAR CONFIGURACIÓN
  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      // Limpieza de entradas
      const normalizedCelular = celularNumber.trim();
      const normalizedEmail = customEmail.trim();

      // Resetear errores
      setCelularError(false);
      setCountryCodeError(false);
      setEmailError(false);

      // Validaciones
      if (normalizedCelular && normalizedCelular.length !== 10) {
        setCelularError(true);
        message.error("El número de celular debe tener 10 dígitos");
        setLoading(false);
        return;
      }

      if (normalizedCelular && !selectedCountryCode) {
        setCountryCodeError(true);
        message.error("Debes seleccionar un código de país");
        setLoading(false);
        return;
      }

      if (normalizedEmail && !validateEmail(normalizedEmail)) {
        setEmailError(true);
        message.error("Por favor ingresa un correo electrónico válido");
        setLoading(false);
        return;
      }

      // Si el celular está vacío, forzamos enableCelular a false
      const finalEnableCelular = normalizedCelular ? enableCelular : false;

      // Si el correo está vacío, forzamos enableEmail a false
      const finalEnableEmail = normalizedEmail ? enableEmail : false;

      // Enviamos todo junto
      const payload = {
        establishmentID,
        name_establishment,
        celularNumber: normalizedCelular ? `${selectedCountryCode.replace('+', '')}-${normalizedCelular}` : '',
        enableCelular: finalEnableCelular,
        email: normalizedEmail,
        enableEmail: finalEnableEmail
      };

      await post('/notification-settings', payload);
      message.success("¡Configuración de notificaciones guardada exitosamente!");
    } catch (error) {
      console.error("Error al guardar configuración de notificaciones:", error);
      message.error("Ocurrió un error guardando la configuración.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="notification-settings-container">
      <Card className="main-card">
        <div className="title-container">
          <div className="icon-container">
            <MailOutlined />
          </div>
          <Title level={3} className="section-title">
            Configuración de Notificaciones
          </Title>
          <Text type="secondary" className="section-subtitle">
            Configura cómo deseas recibir las notificaciones de tu establecimiento
          </Text>
        </div>

        {initialLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0' }}>
            <Spin size="large" tip="Cargando configuración..." />
          </div>
        ) : (
          <>
            {/* Sección para Correo */}
            <Card className="section-card">
              <div className="section-header">
                <div className="section-icon">
                  <MailOutlined />
                </div>
                <div>
                  <Title level={4} className="section-title">
                    Correo para notificaciones
                  </Title>
                  <Text type="secondary" className="section-subtitle">
                    Ingresa el correo con el que deseas recibir las notificaciones
                  </Text>
                </div>
              </div>
              <div className="section-content">
                <Input
                  placeholder="ejemplo@correo.com"
                  value={customEmail}
                  onChange={(e) => {
                    setCustomEmail(e.target.value);
                    setEmailError(false);
                  }}
                  status={emailError ? "error" : ""}
                  style={{ width: '100%' }}
                  size="large"
                  disabled={loading}
                />
                <div className="switch-container">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Switch
                      checked={enableEmail}
                      disabled={!customEmail?.trim() || loading}
                      onChange={(checked) => setEnableEmail(checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <Text style={{ margin: 0 }}>Activar notificaciones por correo</Text>
                  </div>
                  {enableEmail ? (
                    <Tag color="success" style={{ margin: 0, padding: '4px 8px' }}>Activado</Tag>
                  ) : (
                    <Tag color="error" style={{ margin: 0, padding: '4px 8px' }}>Desactivado</Tag>
                  )}
                </div>
              </div>
            </Card>

            {/* Sección para Celular */}
            <Card className="section-card">
              <div className="section-header">
                <div className="section-icon">
                  <PhoneOutlined />
                </div>
                <div>
                  <Title level={4} className="section-title">
                    Celular para notificaciones
                  </Title>
                  <Text type="secondary" className="section-subtitle">
                    Ingresa el celular con el que deseas recibir las notificaciones
                  </Text>
                </div>
              </div>
              <div className="section-content">
                <Space.Compact style={{ width: '100%' }}>
                  <Select
                    value={selectedCountryCode || undefined}
                    onChange={(value) => {
                      setSelectedCountryCode(value ? `+${value}` : '');
                      setCountryCodeError(false);
                    }}
                    style={{ width: '200px' }}
                    showSearch
                    placeholder="Selecciona el país"
                    optionFilterProp="children"
                    status={countryCodeError ? "error" : ""}
                    size="large"
                    disabled={loading}
                    filterOption={(input, option) => {
                      const country = countries.find(c => c.phone_code === option?.value);
                      if (!country) return false;
                      return (
                        country.name.toLowerCase().includes(input.toLowerCase()) ||
                        country.iso2.toLowerCase().includes(input.toLowerCase()) ||
                        country.phone_code.includes(input)
                      );
                    }}
                  >
                    {countries.map(country => (
                      <Option key={country.iso2} value={country.phone_code}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span className={`fi fi-${country.iso2.toLowerCase()}`} style={{ width: '20px', height: '15px' }} />
                          <span>{country.name}</span>
                          <span style={{ color: '#999' }}>(+{country.phone_code})</span>
                        </div>
                      </Option>
                    ))}
                  </Select>
                  <Input
                    placeholder="Ej: 1234567890"
                    value={celularNumber}
                    maxLength={10}
                    status={celularError ? "error" : ""}
                    onKeyDown={(e) => {
                      if (
                        e.key === 'Backspace' ||
                        e.key === 'Delete' ||
                        e.key === 'ArrowLeft' ||
                        e.key === 'ArrowRight' ||
                        e.key === 'Tab' ||
                        e.key === 'Home' ||
                        e.key === 'End' ||
                        /[0-9]/.test(e.key)
                      ) {
                        return;
                      }
                      e.preventDefault();
                    }}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setCelularNumber(value);
                      setCelularError(false);
                    }}
                    style={{ flex: 1 }}
                    size="large"
                    disabled={loading}
                  />
                </Space.Compact>
                <div className="switch-container">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Switch
                      checked={enableCelular}
                      disabled={!celularNumber?.trim() || loading}
                      onChange={(checked) => setEnableCelular(checked)}
                      style={{ marginRight: '8px' }}
                    />
                    <Text style={{ margin: 0 }}>Activar notificaciones por celular</Text>
                  </div>
                  {enableCelular ? (
                    <Tag color="success" style={{ margin: 0, padding: '4px 8px' }}>Activado</Tag>
                  ) : (
                    <Tag color="error" style={{ margin: 0, padding: '4px 8px' }}>Desactivado</Tag>
                  )}
                </div>
              </div>
            </Card>

            {/* Botón Guardar configuración */}
            <div className="save-button-container">
              <Button
                type="primary"
                onClick={handleSaveSettings}
                loading={loading}
                size="large"
                className="save-button"
              >
                Guardar configuración
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default NotificationSettings;
